import React, { createContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { checkChildren, filterChildrenOnType } from '@hooks';
import { mergeClasses } from '@utils';

import CheckboxDescription from './Checkbox.Description';
import CheckboxErrorMessage from './Checkbox.ErrorMessage';
import CheckboxLabel from './Checkbox.Label';

export const CheckboxContext = createContext();

/**
 * Checkbox component that serves as a container for its child components:
 * `Checkbox.Description`, `Checkbox.ErrorMessage`, `Checkbox.Label`.
 * Requires at least one child of type `Checkbox.ErrorMessage` and `Checkbox.Label`.
 *
 * @component Checkbox
 * @param {Object} props - The properties for the Checkbox component.
 * @param {React.ReactNode} props.children - The child components of the Checkbox. Must include at least one `Checkbox.ErrorMessage` and `Checkbox.Label` child component.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the Checkbox container.
 * @param {boolean} [props.isValid] - Optional flag to indicate if the Checkbox is valid.
 * @param {Object} [restProps] - Additional properties to be passed to the Checkbox container.
 * @returns {ReactElement} The rendered Checkbox component.
 * @example
 * <Checkbox isValid={true}>
 *   <Checkbox.Label>
 *     I have read and understood the Terms of Trade
 *   </Checkbox.Label>
 *   <Checkbox.Description>
 *     The following Terms of Trade is applicable to you: <a href="#">Unisport</a> and <a href="#">Niké</a>.
 *   </Checkbox.Description>
 *   <Checkbox.ErrorMessage>
 *     You must accept the Terms of Trade to continue.
 *   </Checkbox.ErrorMessage>
 * </Checkbox>
 */
export default function Checkbox({
  children,
  className,
  isValid,
  ...restProps
}) {
  checkChildren(children, [
    Checkbox.Description,
    Checkbox.ErrorMessage,
    Checkbox.Label
  ]);

  let hasErrorMessage = false;
  let hasLabel = false;

  React.Children.forEach(children, child => {
    if (child.type === Checkbox.ErrorMessage) {
      hasErrorMessage = true;
    }
    if (child.type === Checkbox.Label) {
      hasLabel = true;
    }
  });

  if (!hasErrorMessage) {
    throw new Error(
      "Missing required components in 'Checkbox'. 'Checkbox.ErrorMessage' component is required."
    );
  }

  if (!hasLabel) {
    throw new Error(
      "Missing required components in 'Checkbox'. 'Checkbox.Label' component is required."
    );
  }

  const contextValue = useMemo(
    () => ({
      isValid
    }),
    [isValid]
  );

  const [labelChild, nonLabelChildren] = filterChildrenOnType(
    [Checkbox.Label],
    children
  );

  return (
    <CheckboxContext.Provider value={contextValue}>
      <div className="space-y-0.5">
        <label className="flex cursor-pointer items-center space-x-2">
          <input
            className={mergeClasses(
              'form-checkbox m-0 h-6 w-6 cursor-pointer rounded border border-solid border-black checked:bg-green checked:hover:bg-green focus:ring-0 focus:ring-transparent focus:ring-offset-0 focus:ring-offset-transparent checked:focus:bg-green focus-visible:ring-1 focus-visible:ring-black',
              {
                'border-cinnebar focus:border-cinnebar focus:ring-cinnebar':
                  isValid === false
              },
              className
            )}
            type="checkbox"
            {...restProps}
          />
          {labelChild}
        </label>
        {nonLabelChildren}
      </div>
    </CheckboxContext.Provider>
  );
}

Checkbox.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  isValid: PropTypes.bool
};

Checkbox.defaultProps = {
  className: '',
  isValid: undefined
};

Checkbox.Description = CheckboxDescription;
Checkbox.ErrorMessage = CheckboxErrorMessage;
Checkbox.Label = CheckboxLabel;
